import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {config} from '@bitstillery/common/app'
import {page_copy, type PageCopyKind} from '@/cms/lib/page_meta'

const HERO_BG_FADE_MS = 400

/**
 * The page's `hero_background` is stored as a host-independent media-library `uri` (e.g. `v5/<id>`) so it
 * resolves per environment. Absolute URLs and static `/img/…` asset paths pass through unchanged; a bare
 * media uri gets the current environment's media host prepended.
 */
export function resolve_hero_background_url(value: string): string {
    if (!value || /^(https?:)?\/\//.test(value) || value.startsWith('/')) {
        return value
    }
    return `${config.media_photo_host}/${value}`
}

/**
 * Shared with funnel `HeroBanner` and portal layout chrome: one global “committed” URL so
 * navigations stay visually continuous. Two stacked layers crossfade so the outgoing image
 * remains visible until the incoming image is opaque (no flash through the hero base color).
 */
let last_committed_hero_url = ''

let hero_fade_generation = 0

export interface HeroBackgroundImageAttrs {
    /** Base class: becomes `…-stack` (wrapper) and `…-layer` (each photo plane). */
    className: string
    image_url: string
}

export class HeroBackgroundImage extends MithrilComponent<HeroBackgroundImageAttrs> {
    private bound_url = ''
    private bottom_url = ''
    private bottom_opacity = 1
    private top_url = ''
    private top_opacity = 0
    private fade_timer: ReturnType<typeof setTimeout> | null = null
    /**
     * Collapsing after a crossfade changes opacities and URLs in one tick; without this, the
     * stylesheet’s `transition: opacity` would run a second fade (blink) on the bottom layer.
     */
    private layer_transition_suppressed = false
    /**
     * True once this instance has committed a first background. The first apply (initial load /
     * hydration) snaps so the hero paints immediately without a fade-in flash; every later apply
     * animates — including to/from the empty state, which is what crossfades the SVG ribbon backdrop
     * and a page photo into each other on navigation (empty→photo fades the photo in over the SVG;
     * photo→empty fades it out to reveal the SVG).
     */
    private primed = false

    override oninit(vnode: m.Vnode<HeroBackgroundImageAttrs>) {
        // SSR has no DOM and runs none of the crossfade's async callbacks, so the animation
        // machinery would serialize the stale module-global `last_committed_hero_url` left by a
        // prior request in the same process. Render the current image directly instead, and
        // never read or mutate the cross-request global on the server.
        if (globalThis.__SSR_MODE__) {
            this.bound_url = vnode.attrs.image_url || ''
            this.bottom_url = this.bound_url
            this.bottom_opacity = 1
            this.top_url = ''
            this.top_opacity = 0
            return
        }
        this.apply_url(vnode.attrs.image_url || '')
    }

    override onupdate(vnode: m.Vnode<HeroBackgroundImageAttrs>) {
        const next = vnode.attrs.image_url || ''
        if (next !== this.bound_url) {
            this.apply_url(next)
        }
    }

    override onbeforeremove() {
        hero_fade_generation += 1
        this.clear_fade_timer()
    }

    private clear_fade_timer() {
        if (this.fade_timer !== null) {
            clearTimeout(this.fade_timer)
            this.fade_timer = null
        }
    }

    /** Collapse both layers onto a single committed image with no animation (transition suppressed for one frame). */
    private commit(next: string) {
        this.layer_transition_suppressed = true
        last_committed_hero_url = next
        this.bottom_url = next
        this.bottom_opacity = 1
        this.top_url = ''
        this.top_opacity = 0
        m.redraw()
        requestAnimationFrame(() => {
            this.layer_transition_suppressed = false
            m.redraw()
        })
    }

    private apply_url(next: string) {
        this.bound_url = next

        // First background ever committed by this (persistent) instance → snap; later ones animate.
        const first = !this.primed
        this.primed = true

        const interrupted = this.fade_timer !== null
        this.clear_fade_timer()

        // Re-establish the stable frame (committed image on bottom, nothing on top). When a timed
        // fade was interrupted, suppress the transition so the bottom doesn't animate 0→1 again.
        this.layer_transition_suppressed = interrupted
        this.bottom_url = last_committed_hero_url
        this.bottom_opacity = 1
        this.top_url = ''
        this.top_opacity = 0
        if (interrupted) {
            m.redraw()
            requestAnimationFrame(() => {
                this.layer_transition_suppressed = false
                m.redraw()
            })
        }

        const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

        // Snap on the first paint (no flash), when the image is unchanged, or when motion is reduced.
        // Note we NO LONGER snap merely because there's no prior image: an empty→photo change now
        // crossfades the incoming photo in over the SVG backdrop (bottom layer is empty, so the top
        // photo fades up while the SVG shows through beneath it).
        if (first || last_committed_hero_url === next || reduced) {
            this.commit(next)
            return
        }

        this.begin_background_change(next)
    }

    private begin_background_change(next: string) {
        hero_fade_generation += 1
        const generation = hero_fade_generation
        const stale = () => generation !== hero_fade_generation

        // Empty target: fade the current image out to the hero base color, then commit empty.
        if (!next) {
            m.redraw()
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (stale()) {
                        return
                    }
                    this.bottom_opacity = 0
                    m.redraw()
                    this.fade_timer = setTimeout(() => {
                        this.fade_timer = null
                        if (stale()) {
                            return
                        }
                        this.commit('')
                    }, HERO_BG_FADE_MS)
                })
            })
            return
        }

        // Crossfade the new image in over the outgoing one, then commit it to the bottom layer.
        const run_crossfade_in = () => {
            if (stale()) {
                return
            }
            this.top_url = next
            this.top_opacity = 0
            this.bottom_opacity = 1
            m.redraw()
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (stale()) {
                        return
                    }
                    this.bottom_opacity = 0
                    this.top_opacity = 1
                    m.redraw()
                    this.fade_timer = setTimeout(() => {
                        this.fade_timer = null
                        if (stale()) {
                            return
                        }
                        this.commit(next)
                    }, HERO_BG_FADE_MS)
                })
            })
        }

        // Preload first so the crossfade reveals a fully-decoded image rather than a blank plane.
        const img = new Image()
        img.onload = () => {
            void img.decode().catch(() => {})
            run_crossfade_in()
        }
        img.onerror = run_crossfade_in
        img.src = next
    }

    view(vnode: m.Vnode<HeroBackgroundImageAttrs>) {
        const no_transition = this.layer_transition_suppressed ? ({transition: 'none'} as const) : {}
        const bottom_style = {
            ...(this.bottom_url ? {backgroundImage: `url(${this.bottom_url})`} : {}),
            opacity: this.bottom_opacity,
            ...no_transition,
        }
        const top_style = {
            ...(this.top_url ? {backgroundImage: `url(${this.top_url})`} : {}),
            opacity: this.top_opacity,
            ...no_transition,
        }

        return (
            <div className={`${vnode.attrs.className}-stack`}>
                <div className={`${vnode.attrs.className}-layer`} style={bottom_style} />
                <div className={`${vnode.attrs.className}-layer`} style={top_style} />
            </div>
        )
    }
}

export interface HeroBannerAttrs {
    /**
     * The compact hero variant — the photo shows only behind the glass header (flush with the
     * breadcrumb bar). The shop layout always renders it; CMS pages select it per page via
     * `hero_mode` to give the content below more vertical room (e.g. `/` for authenticated users →
     * more space for products).
     */
    collapsed?: boolean
    hero_background: string
    /**
     * Which copy family the title/subtitle come from: a CMS page's own page-bound copy, the slug-keyed
     * `shell.*` copy of a route with no CMS page (shop, my-portal), or the global 404 copy when the
     * path resolves to no page at all. Not derivable from `path`, which the shop and my-portal also
     * leave unset.
     */
    copy_kind?: PageCopyKind
    /** Override with the embedded iframe postMessage */
    path?: string | undefined
}

/**
 * One component for both hero modes: the collapsed/full switch is a class toggle on a persistent
 * element (not two components), so navigating between pages with different `hero_mode` animates the
 * band height via the stylesheet's transition instead of tearing down and rebuilding the hero. The
 * title block is omitted entirely in collapsed mode — the collapsed band has no title/subtitle, and
 * rendering it would call `$t` with page keys that don't exist for shop pages (missingKey warnings).
 */
/**
 * Flowing colour ribbons behind the collapsed glass bar — real wave paths, which CSS gradients can't
 * draw (they only make blobs, bands and cones). Rendered INLINE (not a `background-image` SVG) for one
 * reason: an external SVG is an isolated document and can't read the page's CSS variables, but an
 * inline one inherits them — so the gradient hues ride `--h-info` (the top bar's teal) and `--h-brand`,
 * and the whitelabel brand (etr) gets its own tweaked colours for free. Stretched by CSS to any width
 * with no tiling (the exact thing Midjourney couldn't do at extreme ratios), then blurred into soft
 * aurora ribbons — an echo of the old portal's "brands swirling round a globe" landing.
 */
function HeroRibbons(): m.Children {
    // stop-color is set via `style` (a CSS property) rather than the `stop-color` attribute, because
    // var()/oklch() only resolve in CSS values, not in presentation attributes. Lightness/chroma are
    // tuned for vivid ribbons; only the HUE comes from the theme, so it stays on-brand per whitelabel.
    const s = (color: string) => ({stopColor: color})
    // The ribbons render as a STATIC warped/blurred aurora. Animating the filter (boiling the turbulence
    // baseFrequency + pulsing the displacement scale) was dropped: animating an feTurbulence recomputes
    // the whole fractal-noise field on the CPU every frame, which pegged a core on low-end machines for a
    // motion most visitors never noticed. A static filter is rasterised once and cached.
    const d_main =
        'M-60 38 C 100 16 180 14 240 14 C 340 14 420 40 480 43 C 560 46 640 18 720 18 C 820 18 900 43 960 45 C 1060 47 1120 22 1200 22 C 1320 22 1420 38 1500 40 L1500 52 C 1420 52 1320 54 1200 54 C 1120 54 1060 55 960 55 C 900 55 820 50 720 50 C 640 50 560 53 480 53 C 420 53 340 46 240 46 C 180 46 100 50 -60 50 Z'
    const d_accent =
        'M-40 24 C 220 14 360 28 560 24 C 780 19 900 12 1120 18 C 1300 23 1400 32 1500 28 L1500 36 C 1400 40 1300 31 1120 26 C 900 20 780 28 560 32 C 360 36 220 24 -40 32 Z'
    // ONE broad twisting ribbon carries the colour, not several overlapping strokes: overlapping
    // different-hued strokes mix where they cross (red + azure = the purple mud that showed on the
    // left), whereas a single shape with a gradient flowing along its length (azure → cyan → red →
    // cyan → azure) transitions colour smoothly with no mixing — the "smooth twist" the right side
    // already had, now over the full width. Its width swells and pinches (a twisting ribbon, edge-on
    // at the pinches), and the gradient stops are aligned to the swells so a colour sits on each
    // "face". A thin second ribbon rides above for depth, kept low-opacity so it doesn't muddy.
    // Hues from the logo/theme: RED (--h-brand, 14deg for MSP), AZURE/CYAN (--h-chrome nudged +/-).
    // --h-chrome, not --h-info: the backdrop is a chrome band (like the header/footer/cart), so the
    // ribbons must sit on the same teal the band's dark base already uses.
    // Light internal blur (6); the bar's 12px backdrop-blur does the frosting.
    return (
        <svg className='hero-ribbons' viewBox='0 0 1440 72' preserveAspectRatio='none' aria-hidden='true' focusable='false'>
            <defs>
                <linearGradient id='hero-ribbon-flow' x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' style={s('oklch(60% 0.16 calc(var(--h-chrome) + 12deg))')} />
                    <stop offset='0.18' style={s('oklch(77% 0.13 calc(var(--h-chrome) - 20deg))')} />
                    <stop offset='0.5' style={s('oklch(62% 0.19 var(--h-brand))')} />
                    <stop offset='0.82' style={s('oklch(75% 0.13 calc(var(--h-chrome) - 18deg))')} />
                    <stop offset='1' style={s('oklch(60% 0.16 calc(var(--h-chrome) + 10deg))')} />
                </linearGradient>
                <linearGradient id='hero-ribbon-accent' x1='0' y1='0' x2='1' y2='0'>
                    <stop offset='0' style={s('oklch(78% 0.12 calc(var(--h-chrome) - 22deg))')} />
                    <stop offset='0.6' style={s('oklch(66% 0.18 var(--h-brand))')} />
                    <stop offset='1' style={s('oklch(62% 0.16 calc(var(--h-chrome) + 10deg))')} />
                </linearGradient>
                {/* Ribbon filter: organic warp (turbulence-driven displacement) → frost blur → BLOOM
                    (a heavy blur screened back over, so bright parts glow). Static — rasterised once and
                    cached; see the note in the component on why the turbulence no longer animates. */}
                <filter id='hero-ribbon-fx' x='-10%' y='-170%' width='120%' height='440%'>
                    <feTurbulence type='fractalNoise' baseFrequency='0.006 0.017' numOctaves='2' seed='7' result='noise' />
                    <feDisplacementMap
                        in='SourceGraphic'
                        in2='noise'
                        scale='16'
                        xChannelSelector='R'
                        yChannelSelector='G'
                        result='warped'
                    />
                    <feGaussianBlur in='warped' stdDeviation='4' result='base' />
                    <feGaussianBlur in='warped' stdDeviation='11' result='halo' />
                    <feBlend in='base' in2='halo' mode='screen' />
                </filter>
            </defs>
            {/* Both ribbons share one warp filter (so they stay in sync). The accent screen-blends where
                it crosses the main ribbon so the colours add to light rather than muddying. */}
            <g filter='url(#hero-ribbon-fx)'>
                <path fill='url(#hero-ribbon-flow)' opacity='0.92' d={d_main} />
                <path fill='url(#hero-ribbon-accent)' opacity='0.6' d={d_accent} style={{mixBlendMode: 'screen'}} />
            </g>
        </svg>
    )
}

export class HeroBanner extends MithrilComponent<HeroBannerAttrs> {
    view(vnode: m.Vnode<HeroBannerAttrs>) {
        // The collapsed band carries no photo — it shows the themed procedural backdrop (`.hero-backdrop`:
        // dark base + specular glass highlights in CSS, plus the ribbon SVG). Passing an empty url fades
        // any outgoing full-hero photo out to that backdrop (a photo → procedural crossfade on cms→shop).
        const background = vnode.attrs.collapsed ? '' : vnode.attrs.hero_background
        const copy = page_copy(vnode.attrs.copy_kind ?? 'shell')
        return (
            <div className={classes('hero', {'hero-collapsed': vnode.attrs.collapsed})}>
                <div className='hero-backdrop'>{HeroRibbons()}</div>
                <HeroBackgroundImage className='hero-bg' image_url={resolve_hero_background_url(background)} />
                {!vnode.attrs.collapsed && (
                    <div className='hero-content'>
                        <h1>{copy.title}</h1>
                        <p className='hero-subtitle'>{copy.description}</p>
                    </div>
                )}
            </div>
        )
    }
}
